import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const ConversionCalculatorPage = () => {
  const [category, setCategory] = useState('length');
  const [fromValue, setFromValue] = useState('');
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [result, setResult] = useState(null);

  const conversions = {
    length: {
      units: ['meters', 'kilometers', 'miles', 'feet', 'inches', 'yards', 'centimeters', 'millimeters'],
      toBase: { meters: 1, kilometers: 1000, miles: 1609.34, feet: 0.3048, inches: 0.0254, yards: 0.9144, centimeters: 0.01, millimeters: 0.001 }
    },
    weight: {
      units: ['kilograms', 'grams', 'pounds', 'ounces', 'milligrams', 'tons'],
      toBase: { kilograms: 1, grams: 0.001, pounds: 0.453592, ounces: 0.0283495, milligrams: 0.000001, tons: 907.185 }
    },
    temperature: {
      units: ['celsius', 'fahrenheit', 'kelvin'],
      special: true
    },
    volume: {
      units: ['liters', 'milliliters', 'gallons', 'quarts', 'pints', 'cups', 'fluid_ounces'],
      toBase: { liters: 1, milliliters: 0.001, gallons: 3.78541, quarts: 0.946353, pints: 0.473176, cups: 0.236588, fluid_ounces: 0.0295735 }
    },
    area: {
      units: ['square_meters', 'square_feet', 'square_yards', 'acres', 'hectares', 'square_kilometers', 'square_miles'],
      toBase: { square_meters: 1, square_feet: 0.092903, square_yards: 0.836127, acres: 4046.86, hectares: 10000, square_kilometers: 1000000, square_miles: 2589988 }
    },
    speed: {
      units: ['meters_per_second', 'kilometers_per_hour', 'miles_per_hour', 'knots', 'feet_per_second'],
      toBase: { meters_per_second: 1, kilometers_per_hour: 0.277778, miles_per_hour: 0.44704, knots: 0.514444, feet_per_second: 0.3048 }
    }
  };

  const convert = () => {
    const value = parseFloat(fromValue);
    if (isNaN(value)) return;

    let resultValue;

    if (category === 'temperature') {
      if (fromUnit === 'celsius') {
        if (toUnit === 'fahrenheit') resultValue = (value * 9/5) + 32;
        else if (toUnit === 'kelvin') resultValue = value + 273.15;
        else resultValue = value;
      } else if (fromUnit === 'fahrenheit') {
        if (toUnit === 'celsius') resultValue = (value - 32) * 5/9;
        else if (toUnit === 'kelvin') resultValue = (value - 32) * 5/9 + 273.15;
        else resultValue = value;
      } else if (fromUnit === 'kelvin') {
        if (toUnit === 'celsius') resultValue = value - 273.15;
        else if (toUnit === 'fahrenheit') resultValue = (value - 273.15) * 9/5 + 32;
        else resultValue = value;
      }
    } else {
      const conv = conversions[category];
      const baseValue = value * conv.toBase[fromUnit];
      resultValue = baseValue / conv.toBase[toUnit];
    }

    setResult(resultValue.toFixed(6).replace(/\.?0+$/, ''));
  };

  const currentUnits = conversions[category].units;

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Conversion Calculator</span>
          </nav>
          <h1 className="text-3xl font-bold text-white mb-2">Unit Converter</h1>
          <p className="text-gray-400 mb-8">Convert between different units of measurement.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="mb-5">
              <label className="block text-gray-300 mb-2 font-medium">Category</label>
              <select value={category} onChange={(e) => { setCategory(e.target.value); setFromUnit(''); setToUnit(''); setResult(null); }} className="w-full px-4 py-2.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg text-white">
                <option value="length">Length</option>
                <option value="weight">Weight</option>
                <option value="temperature">Temperature</option>
                <option value="volume">Volume</option>
                <option value="area">Area</option>
                <option value="speed">Speed</option>
              </select>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              <div>
                <label className="block text-gray-300 mb-2 font-medium">Value</label>
                <Input type="number" value={fromValue} onChange={(e) => setFromValue(e.target.value)} className="w-full bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="1" />
              </div>
              <div>
                <label className="block text-gray-300 mb-2 font-medium">From</label>
                <select value={fromUnit} onChange={(e) => setFromUnit(e.target.value)} className="w-full px-4 py-2.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg text-white">
                  <option value="">Select unit</option>
                  {currentUnits.map(u => <option key={u} value={u}>{u.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-gray-300 mb-2 font-medium">To</label>
                <select value={toUnit} onChange={(e) => setToUnit(e.target.value)} className="w-full px-4 py-2.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg text-white">
                  <option value="">Select unit</option>
                  {currentUnits.map(u => <option key={u} value={u}>{u.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
            </div>
            <Button onClick={convert} className="mt-5 bg-cyan-600 hover:bg-cyan-500 text-white">Convert</Button>
            {result && (
              <div className="mt-6 p-5 bg-cyan-500/10 border border-cyan-500/30 rounded-xl text-center">
                <p className="text-gray-400">{fromValue} {fromUnit.replace(/_/g, ' ')} =</p>
                <p className="text-3xl font-bold text-cyan-400">{result} {toUnit.replace(/_/g, ' ')}</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ConversionCalculatorPage;
